import React, { useEffect, useRef, useState } from 'react';
import { createChart, LineSeries, CandlestickSeries, UTCTimestamp } from 'lightweight-charts';
import { ISubnetChartDataResponse } from '../utils/types';
import { useTheme } from '../context/ThemeContext';
import { apiServices } from '../utils/apiServices';
import { useParams } from 'react-router-dom';
import { RefreshIcon } from './SVGIcons';
import clsx from 'clsx';

const INTERVALS = [
  { label: '1m', value: '1m' },
  { label: '5m', value: '5m' },
  { label: '1h', value: '1h' },
  { label: '1d', value: '1d' },
];

const TradingChart = () => {
  const { id } = useParams<{ id: string }>();
  const [chartData, setChartData] = useState<ISubnetChartDataResponse[]>([]);
  const [chartInterval, setChartInterval] = useState('5m');
  const { theme } = useTheme();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const fetchChartData = async () => {
    if (!id) return;
    setIsRefreshing(true);
    try {
      const data = await apiServices.price.chart(id, chartInterval, 0, Date.now());
      setChartData(data);
    } catch (error) {
      console.error('Failed to fetch chart data:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchChartData();
    // const intervalId = setInterval(() => {
    //   if (id) {
    //     fetchChartData();
    //   } 
    // }, 60000); // 1 minute in milliseconds

    // return () => clearInterval(intervalId); // Cleanup on unmount
  }, [id, chartInterval]);

  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<any>(null);

  useEffect(() => {
    if (!chartContainerRef.current) return;

    // Get container dimensions
    const container = chartContainerRef.current;
    const containerWidth = container.clientWidth;

    const chart = createChart(container, {
      width: containerWidth,
      height: 300,
      layout: {
        background: { color: 'transparent' },
        textColor: theme === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
      },
      grid: {
        vertLines: { visible: false },
        horzLines: { visible: false },
      },
      crosshair: {
        mode: 1,
        vertLine: {
          color: theme === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
          width: 1,
          style: 0,
          labelBackgroundColor: theme === 'dark' ? '#262626' : '#F2F2F2',
        },
        horzLine: {
          color: theme === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
          width: 1,
          style: 0,
          labelBackgroundColor: theme === 'dark' ? '#262626' : '#F2F2F2',
        },
      },
      rightPriceScale: {
        borderColor: theme === 'dark' ? '#374151' : '#e5e7eb',
        textColor: theme === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
      },
      leftPriceScale: {
        borderColor: theme === 'dark' ? '#374151' : '#e5e7eb',
        textColor: theme === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
      },
    });

    chartRef.current = chart;

    // Add area series for shadow effect
    const candlestickSeries = chart.addSeries(
      CandlestickSeries,
      {
        priceFormat: {
          type: 'price',
          precision: 6,
          minMove: 0.000001,
        },
        upColor: theme === 'dark' ? '#10b981' : '#059669',
        downColor: theme === 'dark' ? '#ef4444' : '#dc2626',
        borderUpColor: theme === 'dark' ? '#10b981' : '#059669',
        borderDownColor: theme === 'dark' ? '#ef4444' : '#dc2626',
        wickUpColor: theme === 'dark' ? '#10b981' : '#059669',
        wickDownColor: theme === 'dark' ? '#ef4444' : '#dc2626',
      }
    );


    chart.applyOptions({
      timeScale: {
        timeVisible: true,
        secondsVisible: true,
        rightOffset: 12,
        barSpacing: 3,
        fixLeftEdge: true,
        lockVisibleTimeRangeOnResize: true,
        rightBarStaysOnScroll: true,
        borderVisible: false,
        visible: true,
        borderColor: theme === 'dark' ? '#374151' : '#e5e7eb',
        tickMarkFormatter: (time: number) => {
          const date = new Date(time * 1000);
          return date.toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          });
        },
      },
      localization: {
        timeFormatter: (time: number) => {
          const date = new Date(time * 1000);
          return date.toLocaleString([], {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
          });
        },
      }
    });

    const formattedData = chartData.map(item => ({
      ...item,
      time: (
        typeof item.time === 'number' && item.time > 1e12
          ? Math.floor(item.time / 1000)
          : item.time
      ) as UTCTimestamp,
    }));

    candlestickSeries.setData(formattedData);

    if (chartInterval === '1d' && formattedData.length > 20) {
      // Show only the last 20 candles
      const lastIndex = formattedData.length - 1;
      chart.timeScale().setVisibleLogicalRange({
        from: lastIndex - 19,
        to: lastIndex,
      });
    }

    // Handle window resize
    const handleResize = () => {
      if (chartRef.current && container) {
        chartRef.current.applyOptions({
          width: container.clientWidth
        });
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      chart.remove();
    };
  }, [chartData, theme, chartInterval]);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={fetchChartData}
            className="hover:bg-black/10 text-black dark:text-white rounded-[100px] dark:hover:bg-white/10 transition-colors text-[14px] w-[40px] sm:w-[46px] h-[40px] sm:h-[46px] flex items-center justify-center"
          >
            <span className={`${isRefreshing ? 'animate-spin' : ''}`}>
              <RefreshIcon fillColor={theme === 'dark' ? 'white' : 'black'} />
            </span>
          </button>
          {INTERVALS.map(({ label, value }) => (
            <button
              key={value}
              onClick={() => setChartInterval(value)}
              // style={{
              //   padding: '6px 16px',
              //   borderRadius: 6,
              //   border: 'none',
              //   background: chartInterval === value
              //     ? (theme === 'dark' ? '#1f2937' : '#e5e7eb')
              //     : 'transparent',
              //   color: chartInterval === value
              //     ? (theme === 'dark' ? '#10b981' : '#059669')
              //     : (theme === 'dark' ? '#d1d5db' : '#374151'),
              //   fontWeight: chartInterval === value ? 700 : 400,
              //   cursor: 'pointer',
              //   outline: chartInterval === value ? '2px solid #10b981' : 'none',
              //   transition: 'all 0.15s',
              // }}
              className={clsx(
                'text-black dark:text-white text-[14px] px-[24px] py-[10px] rounded-[100px] transition-all duration-200 leading-[1.2]',
                chartInterval === value ? 'bg-black/10 dark:bg-white/10' : 'bg-transparent',
              )}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <div ref={chartContainerRef} style={{ width: '100%', height: '300px' }} />
    </div>
  );
};

export default TradingChart;
